import { useEffect, useState } from "react";
import { X, FilePlus2, Search, ArrowLeft, BadgeEuro, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { SelettoreData } from "@/components/SelettoreData";
import { dateIt, eur } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const inputCls = "w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white text-slate-700 " +
  "focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-sky-400 transition-colors duration-200";
const labelCls = "block text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 mb-1";

const SI_NO = ["SI", "NO"];
const SETTORI = ["AUTO", "NON AUTO", "DISINTERMEDIATO"];
const PRIVACY = ["SI+CELL+MAIL", "SI+CELL", "NO"];
const PRIVACY_AP = ["PRESENTE", "ASSENTE"];
const GESTIONE = ["MARTINA", "ELENA", "ALESSIA", "ROSY", "GAIA", "ELEONORA", "GIANLUCA", "DEALER NO AP"];
const GESTIONE_LIQ = ["ROSY", "GAIA", "ELENA", "MARTINA", "ELEONORA"];
const STATI = ["APPROVATA IN PRIMA BATTUTA", "APPROVATA", "APPROVAZIONE VINCOLATA", "IN LAVORAZIONE (NOSTRA)",
  "IN VALUTAZIONE (CENTRO)", "IN ATTESA (DEALER)", "IN ATTESA REGION", "IN ATTESA GIANLUCA",
  "RESPINTA", "RITIRATA"];
const DURATE_CVT = [12, 24, 36, 48, 60, 72, 84];
const DURATE_GGN = [12, 24, 36, 48, 60];

const OBBLIGATORI = [
  ["codice_dealer", "Codice dealer"], ["nome_dealer", "Nome dealer"], ["commerciale", "Commerciale"],
  ["nr_pratica", "Nr pratica"], ["prodotto", "Prodotto"],
  ["settore", "Settore"], ["cognome_nome", "Cognome e nome cliente"], ["privacy", "Privacy"],
  ["privacy_ap", "Privacy A/P"], ["gestione_caricamento", "Gestione caricamento"],
  ["stato_pratica", "Stato pratica"], ["deroga", "Deroga"], ["caricata_dal_dealer", "Caricata dal dealer"],
  ["cpi", "CPI"], ["nfk", "NFK"], ["cvt", "CVT"], ["ggn", "GGN"],
  ["data_ingresso", "Data ingresso"], ["ora_ingresso", "Ora ingresso"],
  ["data_caricamento", "Data caricamento"],
  ["valore_bene", "Valore bene"], ["importo_finanziato", "Importo finanziato"],
  ["istruttoria", "Istruttoria"], ["tasso", "Tasso"], ["durata", "Durata"],
];

const VUOTA = {
  segnala_master: false, nota_segnalazione: "",
  codice_dealer: "", nome_dealer: "", commerciale: "", venditore: "", nr_pratica: "",
  prodotto: "", settore: "", privacy: "", privacy_ap: "", gestione_caricamento: "",
  stato_pratica: "", deroga: "",
  valore_bene: "", anticipo: "", importo_finanziato: "", istruttoria: "", tasso: "", durata: "",
  cpi: "", importo_cpi: "", nfk: "", importo_nfk: "", cvt: "", durata_cvt: "", importo_cvt: "",
  ggn: "", durata_ggn: "", importo_ggn: "",
  caricata_dal_dealer: "",
  data_ingresso: "", ora_ingresso: "", data_caricamento: "", ora_esito: "", data_esito: "",
  data_integrazione: "", ora_integrazione: "", data_approvazione: "", ora_approvazione: "",
  cognome_nome: "", note: "",
};

const SPAN = { 2: "md:col-span-2", 3: "md:col-span-3" };

function campiMancanti(f) {
  const m = OBBLIGATORI.filter(([k]) => !String(f[k] ?? "").trim()).map(([, l]) => l);
  if (f.cpi === "SI" && !String(f.importo_cpi ?? "").trim()) m.push("Importo CPI");
  if (f.nfk === "SI" && !String(f.importo_nfk ?? "").trim()) m.push("Importo NFK");
  if (f.cvt === "SI") {
    if (!String(f.durata_cvt ?? "").trim()) m.push("Durata CVT");
    if (!String(f.importo_cvt ?? "").trim()) m.push("Importo CVT");
  }
  if (f.ggn === "SI") {
    if (!String(f.durata_ggn ?? "").trim()) m.push("Durata GGN");
    if (!String(f.importo_ggn ?? "").trim()) m.push("Importo GGN");
  }
  return m;
}

const sn = (v) => (v === true ? "SI" : v === false ? "NO" : "");

function fromPratica(p) {
  return {
    ...VUOTA,
    codice_dealer: p.dealer_codice || "",
    nome_dealer: p.dealer_nome || "",
    commerciale: p.commerciale_nome_raw || "",
    venditore: p.venditore || "",
    nr_pratica: p.external_id || p.numero_pratica || "",
    prodotto: p.prodotto || "",
    settore: p.settore || p.categoria_macro || "",
    privacy: p.privacy_raw || "",
    privacy_ap: p.privacy_ap || "",
    gestione_caricamento: p.gestione_caricamento || "",
    stato_pratica: p.stato || "",
    deroga: sn(p.deroga),
    valore_bene: p.valore_bene ?? "",
    anticipo: p.anticipo ?? "",
    importo_finanziato: p.importo_finanziato ?? "",
    istruttoria: p.istruttoria ?? "",
    tasso: p.tasso ?? "",
    durata: p.numero_rate ?? "",
    cpi: sn(p.cpi), importo_cpi: p.importo_cpi ?? "",
    nfk: sn(p.nfk), importo_nfk: p.importo_nfk ?? "",
    cvt: sn(p.cvt), durata_cvt: p.durata_cvt ?? "", importo_cvt: p.importo_cvt ?? "",
    ggn: sn(p.ggn), durata_ggn: p.durata_ggn ?? "", importo_ggn: p.importo_ggn ?? "",
    caricata_dal_dealer: sn(p.caricata_dal_dealer),
    data_ingresso: p.data_ingresso || "",
    ora_ingresso: p.ora_ingresso || "",
    data_caricamento: p.data_caricamento || "",
    ora_esito: p.ora_esito || "",
    data_esito: p.data_esito || "",
    data_integrazione: p.data_integrazione || "",
    ora_integrazione: p.ora_integrazione || "",
    data_approvazione: p.data_approvazione || "",
    ora_approvazione: p.ora_approvazione || "",
    note: p.note || "",
    cognome_nome: p.cliente || "",
  };
}

function Field({ label, children, span }) {
  return (
    <div className={SPAN[span] || ""}>
      <label className={labelCls}>{label}</label>
      {children}
    </div>
  );
}

/** Modale di inserimento manuale di una nuova pratica (campi del file "pratiche 2026"). */
export function NuovaPraticaModal({ open, onClose, meta, onSaved, onLiquidazione }) {
  const { user: utenteCorrente } = useAuth();
  const master = utenteCorrente?.role === "MASTER" || utenteCorrente?.role === "ADMIN";
  const [f, setF] = useState(VUOTA);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState("scelta");
  const [modo, setModo] = useState("nuova");
  const [numeroRec, setNumeroRec] = useState("");
  const [cercando, setCercando] = useState(false);
  const [numeroNuova, setNumeroNuova] = useState("");
  const [numeroLiq, setNumeroLiq] = useState("");
  const [numeroDel, setNumeroDel] = useState("");
  const [praticaLiq, setPraticaLiq] = useState(null);
  const [liq, setLiq] = useState({ stato_pratica: "", gestione_liquidazione: "", firma_digitale: "",
    data_richiesta: "", ora_richiesta: "", data_ticket: "", ora_ticket: "", dealer_bloccato: "" });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  // importi incollati dal portale Deutsche Bank: "9.000,00" / "€ 1.234,5" -> 9000 / 1234.5
  const normalizzaImporto = (raw) => {
    const s = String(raw ?? "").replace(/€|\s/g, "").trim();
    if (!s) return "";
    let t = s;
    if (t.includes(",")) t = t.replace(/\./g, "").replace(",", ".");
    else if ((t.match(/\./g) || []).length > 1 || /\.\d{3}$/.test(t)) t = t.replace(/\./g, "");
    const n = Number(t);
    return Number.isFinite(n) ? String(n) : raw;
  };
  const setImporto = (k) => (e) => {
    const v = e.target.value;
    setF((x) => ({ ...x, [k]: /[,€\s]|\.\d{3}(\D|$)/.test(v) ? normalizzaImporto(v) : v }));
  };

  // la data di approvazione (o esito/integrazione) non puo' precedere il caricamento
  const incoerenza = (() => {
    const car = f.data_caricamento;
    if (!car) return null;
    for (const [campo, etichetta] of [["data_approvazione", "approvazione"],
      ["data_esito", "esito"], ["data_integrazione", "integrazione"]]) {
      if (f[campo] && f[campo] < car) {
        return { campo, messaggio: `La data di ${etichetta} (${dateIt(f[campo])}) non puo' essere `
          + `precedente alla data di caricamento (${dateIt(car)})` };
      }
    }
    // ingresso e caricamento sono indipendenti: il dealer puo' caricare oggi e avvisarci domani
    return null;
  })();
  const campo_errato = incoerenza?.campo;

  useEffect(() => {
    if (!open) return;
    setF(VUOTA); setStep("scelta"); setModo("nuova"); setNumeroRec(""); setNumeroNuova("");
    setNumeroLiq(""); setPraticaLiq(null);
    setLiq({ stato_pratica: "", gestione_liquidazione: "", firma_digitale: "",
      data_richiesta: "", ora_richiesta: "", data_ticket: "", ora_ticket: "", dealer_bloccato: "" });
  }, [open]);

  useEffect(() => {
    const cod = f.codice_dealer.trim();
    if (!open || step !== "form" || modo !== "nuova" || cod.length < 4) return;
    let alive = true;
    const t = setTimeout(() => {
      api.get(`/dealers/lookup/${encodeURIComponent(cod)}`).then((r) => {
        const d = r.data;
        if (alive && d) setF((p) => ({ ...p, nome_dealer: d.ragione_sociale || p.nome_dealer,
          commerciale: d.commerciale_nome || p.commerciale }));
      }).catch(() => {});
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [f.codice_dealer, open, step, modo]);

  if (!open) return null;

  const apriNuova = async () => {
    const n = numeroNuova.trim();
    if (!n) return toast.error("Inserisci il numero della nuova pratica");
    setCercando(true);
    try {
      await api.get(`/pratiche/${encodeURIComponent(n)}`);
      toast.error(`La pratica ${n} esiste già: usa «Recupera pratica» per modificarla`);
    } catch (e) {
      if (e?.response?.status === 404) {
        setF({ ...VUOTA, nr_pratica: n }); setModo("nuova"); setStep("form");
      } else {
        toast.error(apiError(e));
      }
    } finally {
      setCercando(false);
    }
  };

  const recupera = async () => {
    const n = numeroRec.trim();
    if (!n) return toast.error("Inserisci il numero della pratica da recuperare");
    setCercando(true);
    try {
      const r = await api.get(`/pratiche/${encodeURIComponent(n)}`);
      setF({ ...fromPratica(r.data.pratica), numero_originale: r.data.pratica.external_id || n });
      setModo("recupera");
      setStep("form");
      toast.success(`Pratica ${n} caricata: modifica i campi e salva`);
    } catch {
      toast.error(`Pratica ${n} non trovata`);
    } finally {
      setCercando(false);
    }
  };

  const cercaLiquidazione = async () => {
    const n = numeroLiq.trim();
    if (!n) return toast.error("Inserisci il numero della pratica da liquidare");
    setCercando(true);
    try {
      const r = await api.get(`/pratiche/${encodeURIComponent(n)}`);
      const p = r.data.pratica;
      setPraticaLiq(p);
      setLiq({
        stato_pratica: p.stato || "",
        gestione_liquidazione: p.gestione_liquidazione || "",
        firma_digitale: sn(p.firma_digitale),
        data_richiesta: p.data_richiesta || "",
        ora_richiesta: p.ora_richiesta || "",
        data_ticket: p.data_ticket || "",
        ora_ticket: p.ora_ticket || "",
        dealer_bloccato: p.dealer_bloccato === true ? "SI" : p.dealer_bloccato === false ? "NO" : "",
      });
      setStep("liquidazione");
    } catch {
      toast.error(`Pratica ${n} non trovata`);
    } finally {
      setCercando(false);
    }
  };

  const elimina = async () => {
    const n = numeroDel.trim();
    if (!n) return;
    if (!window.confirm(`Rimuovere la pratica ${n} dalle pratiche giornaliere?`)) return;
    setSaving(true);
    try {
      await api.delete(`/pratiche/${encodeURIComponent(n)}`);
      toast.success(`Pratica ${n} rimossa`);
      setNumeroDel("");
      onSaved(null, "eliminata");
    } catch (e2) {
      toast.error(apiError(e2));
    } finally {
      setSaving(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const mancanti = campiMancanti(f);
    if (mancanti.length) {
      toast.error(`Campi obbligatori mancanti: ${mancanti.join(", ")}`);
      return;
    }
    if (incoerenza) {
      toast.error(incoerenza.messaggio);
      return;
    }
    setSaving(true);
    try {
      await onSaved(f, modo);
    } finally {
      setSaving(false);
    }
  };

  const mancanti = campiMancanti(f);

  const wrap = "fixed inset-0 z-[1200] flex items-start justify-center overflow-y-auto " +
    "bg-slate-900/50 backdrop-blur-sm px-4 py-8";

  if (step === "scelta") {
    return (
      <div className={wrap} data-testid="nuova-pratica-modal">
        <div className="w-full max-w-2xl rounded-xl bg-white shadow-2xl border border-slate-200">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200
            bg-gradient-to-r from-sky-50 to-white rounded-t-xl">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-sky-700">Gestione manuale</div>
              <h2 className="text-lg font-semibold text-slate-900">Cosa vuoi fare?</h2>
            </div>
            <button type="button" onClick={onClose} data-testid="nuova-pratica-chiudi"
              className="p-2 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors duration-200">
              <X size={18} />
            </button>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="p-5 rounded-xl border-2 border-sky-500 bg-gradient-to-b from-sky-50/70 to-white flex flex-col"
              data-testid="scelta-nuova-pratica">
              <FilePlus2 className="text-sky-600" size={22} />
              <div className="mt-3 font-semibold text-slate-900">Nuova pratica</div>
              <p className="mt-1 mb-3 text-sm text-slate-500 flex-1">Inserisci da zero una pratica non presente negli import.</p>
              <input className={`${inputCls} mt-auto`} value={numeroNuova} data-testid="nuova-numero"
                onChange={(e) => setNumeroNuova(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); apriNuova(); } }}
                placeholder="N° pratica es. 4607436301" />
              <button type="button" onClick={apriNuova} disabled={cercando} data-testid="nuova-apri"
                className="mt-3 w-full text-sm px-4 py-2 rounded-md bg-sky-600 text-white font-semibold
                  hover:bg-sky-700 disabled:opacity-60 transition-colors duration-200">
                {cercando ? "Verifica…" : "Apri form"}
              </button>
            </div>
            <div className="p-5 rounded-xl border-2 border-slate-200 bg-slate-50/60 flex flex-col">
              <Search className="text-slate-500" size={22} />
              <div className="mt-3 font-semibold text-slate-900">Recupera pratica</div>
              <p className="mt-1 mb-3 text-sm text-slate-500 flex-1">Cerca una pratica già inserita per correggerla o aggiornarla.</p>
              <input className={`${inputCls} mt-auto`} value={numeroRec} data-testid="recupera-numero"
                onChange={(e) => setNumeroRec(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); recupera(); } }}
                placeholder="N° pratica es. 4607436301" />
              <button type="button" onClick={recupera} disabled={cercando} data-testid="recupera-cerca"
                className="mt-3 w-full text-sm px-4 py-2 rounded-md bg-slate-900 text-white font-semibold
                  hover:bg-slate-700 disabled:opacity-60 transition-colors duration-200">
                {cercando ? "Ricerca…" : "Recupera"}
              </button>
            </div>
            <div className="p-5 rounded-xl border-2 border-emerald-500 bg-gradient-to-b from-emerald-50/70 to-white flex flex-col">
              <BadgeEuro className="text-emerald-600" size={22} />
              <div className="mt-3 font-semibold text-slate-900">Gestisci liquidazione</div>
              <p className="mt-1 mb-3 text-sm text-slate-500 flex-1">Registra o azzera la liquidazione di una pratica esistente.</p>
              <input className={`${inputCls} mt-auto`} value={numeroLiq} data-testid="liquidazione-numero"
                onChange={(e) => setNumeroLiq(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); cercaLiquidazione(); } }}
                placeholder="N° pratica es. 4607436301" />
              <button type="button" onClick={cercaLiquidazione} disabled={cercando} data-testid="liquidazione-cerca"
                className="mt-3 w-full text-sm px-4 py-2 rounded-md bg-emerald-600 text-white font-semibold
                  hover:bg-emerald-700 disabled:opacity-60 transition-colors duration-200">
                {cercando ? "Ricerca…" : "Cerca pratica"}
              </button>
            </div>
            <div className="p-5 rounded-xl border-2 border-red-400 bg-gradient-to-b from-red-50/60 to-white flex flex-col">
              <Trash2 className="text-red-600" size={22} />
              <div className="mt-3 font-semibold text-slate-900">Elimina pratica</div>
              <p className="mt-1 mb-3 text-sm text-slate-500 flex-1">
                Rimuove la pratica dalle pratiche giornaliere. Se inserita a mano viene eliminata del tutto;
                se arriva dall'estratto della banca resta in archivio (Dashboard 2026) ed esce solo dal giornaliero.
              </p>
              <input className={`${inputCls} mt-auto`} value={numeroDel} data-testid="elimina-numero"
                onChange={(e) => setNumeroDel(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); elimina(); } }}
                placeholder="N° pratica es. 4607436301" />
              <button type="button" onClick={elimina} disabled={saving || !numeroDel.trim()}
                data-testid="elimina-conferma"
                className="mt-3 w-full text-sm px-4 py-2 rounded-md bg-red-600 text-white font-semibold
                  hover:bg-red-700 disabled:opacity-60 transition-colors duration-200">
                {saving ? "Eliminazione…" : "Elimina pratica"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === "liquidazione") {
    const p = praticaLiq || {};
    const setL = (k) => (e) => setLiq((s) => ({ ...s, [k]: e.target.value }));
    const obbl = ["stato_pratica", "gestione_liquidazione", "firma_digitale",
      "data_richiesta", "ora_richiesta", "data_ticket", "ora_ticket", "dealer_bloccato"];
    const liqMancanti = obbl.filter((k) => !String(liq[k] || "").trim()).length;
    const salvaLiq = async (azzera) => {
      setSaving(true);
      try {
        await onLiquidazione(p.external_id, azzera ? { azzera: true } : liq, azzera);
      } finally {
        setSaving(false);
      }
    };
    return (
      <div className={wrap} data-testid="nuova-pratica-modal">
        <div className="w-full max-w-3xl rounded-xl bg-white shadow-2xl border border-slate-200">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200
            bg-gradient-to-r from-emerald-50 to-white rounded-t-xl">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Gestione liquidazione</div>
              <h2 className="text-lg font-semibold text-slate-900">Pratica {p.external_id}</h2>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setStep("scelta")} data-testid="liq-indietro"
                className="inline-flex items-center gap-1 text-sm px-3 py-2 rounded-md border border-slate-200
                  bg-white text-slate-600 hover:text-slate-900 hover:border-slate-300 transition-colors duration-200">
                <ArrowLeft size={15} /> Indietro
              </button>
              <button type="button" onClick={onClose} data-testid="nuova-pratica-chiudi"
                className="p-2 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors duration-200">
                <X size={18} />
              </button>
            </div>
          </div>
          <div className="px-6 py-5 space-y-6">
            <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-5 grid grid-cols-2 md:grid-cols-3 gap-4"
              data-testid="liq-riepilogo">
              {[["Convenzionato", `${p.dealer_nome || "—"} · ${p.dealer_codice || ""}`],
                ["Commerciale", p.commerciale_nome_raw || "—"],
                ["Cliente", p.cliente || "—"],
                ["Importo finanziato", p.importo_finanziato ? eur(p.importo_finanziato) : "n/d"],
                ["Data caricamento", p.data_caricamento ? dateIt(p.data_caricamento) : "n/d"],
                ["Stato attuale", p.stato || "—"]].map(([k, v]) => (
                <div key={k}>
                  <div className={labelCls}>{k}</div>
                  <div className="text-sm text-slate-800 font-medium">{v}</div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Stato pratica *" span={3}>
                <select className={inputCls} value={liq.stato_pratica} data-testid="liq-stato"
                  onChange={setL("stato_pratica")}>
                  <option value="">—</option>
                  {["LIQUIDATA", ...STATI].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Gestione liquidazione *">
                <select className={inputCls} value={liq.gestione_liquidazione} data-testid="liq-gestione"
                  onChange={setL("gestione_liquidazione")}>
                  <option value="">—</option>
                  {GESTIONE_LIQ.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Firma digitale *">
                <select className={inputCls} value={liq.firma_digitale} data-testid="liq-firma"
                  onChange={setL("firma_digitale")}>
                  <option value="">—</option>
                  {SI_NO.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <div />
              <Field label="Data richiesta *">
                <input className={inputCls} type="date" value={liq.data_richiesta} data-testid="liq-data-richiesta"
                  onChange={setL("data_richiesta")} />
              </Field>
              <Field label="Ora richiesta *">
                <input className={inputCls} type="time" value={liq.ora_richiesta} data-testid="liq-ora-richiesta"
                  onChange={setL("ora_richiesta")} />
              </Field>
              <div />
              <Field label="Data ticket *">
                <input className={inputCls} type="date" value={liq.data_ticket} data-testid="liq-data-ticket"
                  onChange={setL("data_ticket")} />
              </Field>
              <Field label="Ora ticket *">
                <input className={inputCls} type="time" value={liq.ora_ticket} data-testid="liq-ora-ticket"
                  onChange={setL("ora_ticket")} />
              </Field>
            </div>
            <div className="flex flex-wrap items-center gap-4 rounded-md border border-slate-200 bg-slate-50 px-4 py-3"
              data-testid="liq-dealer-bloccato">
              <span className="text-sm font-medium text-slate-700">Il dealer è stato bloccato durante questa liquidazione? *</span>
              {SI_NO.map((v) => (
                <label key={v} className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
                  <input type="radio" name="dealer_bloccato" value={v} checked={liq.dealer_bloccato === v}
                    onChange={() => setLiq((s) => ({ ...s, dealer_bloccato: v }))} data-testid={`liq-dealer-bloccato-${v.toLowerCase()}`}
                    className="accent-[#0018A8]" />
                  {v === "SI" ? "Sì" : "No"}
                </label>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
            <button type="button" onClick={() => salvaLiq(true)}
              disabled={saving || !(p.data_liquidazione || p.data_richiesta)}
              data-testid="liq-azzera"
              className="text-sm px-4 py-2 rounded-md border border-red-300 bg-white text-red-600 font-semibold
                hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200">
              Azzera liquidazione
            </button>
            <div className="flex items-center gap-3">
              <button type="button" onClick={onClose} data-testid="liq-annulla"
                className="text-sm px-4 py-2 rounded-md border border-slate-200 bg-white text-slate-600
                  hover:text-slate-900 hover:border-slate-300 transition-colors duration-200">
                Annulla
              </button>
              <button type="button" onClick={() => salvaLiq(false)} disabled={saving || liqMancanti > 0}
                data-testid="liq-salva"
                className="text-sm px-5 py-2 rounded-md bg-emerald-600 text-white font-semibold
                  hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200">
                {saving ? "Salvataggio…" : "Salva liquidazione"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={wrap} data-testid="nuova-pratica-modal">
      <form onSubmit={submit}
        className="w-full max-w-4xl rounded-xl bg-white shadow-2xl border border-slate-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200
          bg-gradient-to-r from-sky-50 to-white rounded-t-xl">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-sky-700">
              {modo === "recupera" ? "Modifica pratica esistente" : "Inserimento manuale"}
            </div>
            <h2 className="text-lg font-semibold text-slate-900">
              {modo === "recupera" ? `Pratica ${f.nr_pratica}` : `Nuova pratica${f.nr_pratica ? ` ${f.nr_pratica}` : ""}`}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setStep("scelta")} data-testid="nuova-pratica-indietro"
              className="inline-flex items-center gap-1 text-sm px-3 py-2 rounded-md border border-slate-200
                bg-white text-slate-600 hover:text-slate-900 hover:border-slate-300 transition-colors duration-200">
              <ArrowLeft size={15} /> Indietro
            </button>
            <button type="button" onClick={onClose} data-testid="nuova-pratica-chiudi"
              className="p-2 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors duration-200">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="px-6 py-5 space-y-6">
          <section>
            <div className="text-xs uppercase tracking-[0.16em] font-bold text-slate-400 mb-3">Convenzionato e pratica</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Codice dealer *">
                <input className={inputCls} value={f.codice_dealer} onChange={set("codice_dealer")}
                  data-testid="np-codice-dealer" placeholder="es. 9545633" />
              </Field>
              <Field label="Nome dealer *" span={2}>
                <input className={inputCls} value={f.nome_dealer} onChange={set("nome_dealer")}
                  data-testid="np-nome-dealer" placeholder="compilato automaticamente dal codice" />
              </Field>
              <Field label="Commerciale *">
                <input className={inputCls} list="np-commerciali" value={f.commerciale} onChange={set("commerciale")}
                  data-testid="np-commerciale" />
                <datalist id="np-commerciali">
                  {(meta?.commerciali || []).map((c) => <option key={c.slug} value={c.nome} />)}
                </datalist>
              </Field>
              <Field label="Venditore">
                <input className={inputCls} value={f.venditore} onChange={set("venditore")} data-testid="np-venditore" />
              </Field>
              <Field label="Nr pratica *">
                <input className={`${inputCls} ${f.nr_pratica && modo === "nuova" ? "bg-slate-100 text-slate-600" : ""}`}
                  value={f.nr_pratica} onChange={set("nr_pratica")} data-testid="np-nr-pratica"
                  readOnly={Boolean(f.nr_pratica) && modo === "nuova"}
                  title={modo === "recupera" ? "Puoi correggere il numero: la pratica verrà salvata con il nuovo numero" : "Numero verificato: non esiste ancora"} />
                {modo === "recupera" && f.numero_originale && f.nr_pratica.trim() !== f.numero_originale && (
                  <div className="text-[11px] text-amber-700 mt-1" data-testid="np-rinumera-avviso">
                    Verrà rinumerata da {f.numero_originale} a {f.nr_pratica.trim()}
                  </div>
                )}
              </Field>
              <Field label="Prodotto *">
                <select className={inputCls} value={f.prodotto} onChange={set("prodotto")} data-testid="np-prodotto">
                  <option value="">—</option>
                  {(meta?.prodotti || []).map((p) => <option key={p.codice} value={p.codice}>{p.label}</option>)}
                </select>
              </Field>
              <Field label="Settore *">
                <select className={inputCls} value={f.settore} onChange={set("settore")} data-testid="np-settore">
                  <option value="">—</option>
                  {SETTORI.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Cognome e nome cliente *">
                <input className={inputCls} value={f.cognome_nome} onChange={set("cognome_nome")} data-testid="np-cliente" />
              </Field>
            </div>
          </section>

          <section>
            <div className="text-xs uppercase tracking-[0.16em] font-bold text-slate-400 mb-3">Gestione e stato</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Privacy *">
                <select className={inputCls} value={f.privacy} onChange={set("privacy")} data-testid="np-privacy">
                  <option value="">—</option>
                  {PRIVACY.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Privacy A/P *">
                <select className={inputCls} value={f.privacy_ap} onChange={set("privacy_ap")} data-testid="np-privacy-ap">
                  <option value="">—</option>
                  {PRIVACY_AP.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Gestione caricamento *">
                <select className={inputCls} value={f.gestione_caricamento} onChange={set("gestione_caricamento")}
                  data-testid="np-gestione">
                  <option value="">—</option>
                  {GESTIONE.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Stato pratica *" span={2}>
                <select className={inputCls} value={f.stato_pratica} onChange={set("stato_pratica")} data-testid="np-stato">
                  <option value="">—</option>
                  {STATI.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Deroga *">
                <select className={inputCls} value={f.deroga} onChange={set("deroga")} data-testid="np-deroga">
                  <option value="">—</option>
                  {SI_NO.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Caricata dal dealer *">
                <select className={inputCls} value={f.caricata_dal_dealer} onChange={set("caricata_dal_dealer")}
                  data-testid="np-caricata-dealer">
                  <option value="">—</option>
                  {SI_NO.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
            </div>
          </section>

          <section>
            <div className="text-xs uppercase tracking-[0.16em] font-bold text-slate-400 mb-3">Piano finanziario</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Valore bene *">
                <input className={`${inputCls} no-spin`} type="text" inputMode="decimal" value={f.valore_bene}
                  onChange={setImporto("valore_bene")} onBlur={(e) => setF((x) => ({ ...x, valore_bene: normalizzaImporto(e.target.value) }))} data-testid="np-valore-bene" />
              </Field>
              <Field label="Anticipo">
                <input className={`${inputCls} no-spin`} type="text" inputMode="decimal" value={f.anticipo}
                  onChange={setImporto("anticipo")} onBlur={(e) => setF((x) => ({ ...x, anticipo: normalizzaImporto(e.target.value) }))} data-testid="np-anticipo" />
              </Field>
              <Field label="Importo finanziato *">
                <input className={`${inputCls} no-spin`} type="text" inputMode="decimal" value={f.importo_finanziato}
                  onChange={setImporto("importo_finanziato")} onBlur={(e) => setF((x) => ({ ...x, importo_finanziato: normalizzaImporto(e.target.value) }))} data-testid="np-importo-finanziato" />
              </Field>
              <Field label="Istruttoria *">
                <input className={`${inputCls} no-spin`} type="text" inputMode="decimal" value={f.istruttoria}
                  onChange={setImporto("istruttoria")} onBlur={(e) => setF((x) => ({ ...x, istruttoria: normalizzaImporto(e.target.value) }))} data-testid="np-istruttoria" />
              </Field>
              <Field label="Tasso *">
                <div className="relative">
                  <input className={`${inputCls} pr-8 no-spin`} type="number" step="0.01" value={f.tasso}
                    onChange={set("tasso")} data-testid="np-tasso" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-500
                    pointer-events-none font-mono">%</span>
                </div>
              </Field>
              <Field label="Durata (rate) *">
                <input className={`${inputCls} no-spin`} type="number" value={f.durata}
                  onChange={set("durata")} data-testid="np-durata" />
              </Field>
            </div>
          </section>

          <section>
            <div className="text-xs uppercase tracking-[0.16em] font-bold text-slate-400 mb-3">Premi assicurativi</div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Field label="CPI *">
                <select className={inputCls} value={f.cpi} onChange={set("cpi")} data-testid="np-cpi">
                  <option value="">—</option>
                  {SI_NO.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label={`Importo CPI${f.cpi === "SI" ? " *" : ""}`}>
                <input className={`${inputCls} no-spin`} type="text" inputMode="decimal" value={f.importo_cpi}
                  onChange={setImporto("importo_cpi")} onBlur={(e) => setF((x) => ({ ...x, importo_cpi: normalizzaImporto(e.target.value) }))} data-testid="np-importo-cpi" />
              </Field>
              <Field label="NFK *">
                <select className={inputCls} value={f.nfk} onChange={set("nfk")} data-testid="np-nfk">
                  <option value="">—</option>
                  {SI_NO.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label={`Importo NFK${f.nfk === "SI" ? " *" : ""}`}>
                <input className={`${inputCls} no-spin`} type="text" inputMode="decimal" value={f.importo_nfk}
                  onChange={setImporto("importo_nfk")} onBlur={(e) => setF((x) => ({ ...x, importo_nfk: normalizzaImporto(e.target.value) }))} data-testid="np-importo-nfk" />
              </Field>
              <Field label="CVT *">
                <select className={inputCls} value={f.cvt} onChange={set("cvt")} data-testid="np-cvt">
                  <option value="">—</option>
                  {SI_NO.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label={`Durata CVT (mesi)${f.cvt === "SI" ? " *" : ""}`}>
                <select className={inputCls} value={f.durata_cvt} onChange={set("durata_cvt")} data-testid="np-durata-cvt">
                  <option value="">—</option>
                  {DURATE_CVT.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label={`Importo CVT${f.cvt === "SI" ? " *" : ""}`}>
                <input className={`${inputCls} no-spin`} type="text" inputMode="decimal" value={f.importo_cvt}
                  onChange={setImporto("importo_cvt")} onBlur={(e) => setF((x) => ({ ...x, importo_cvt: normalizzaImporto(e.target.value) }))} data-testid="np-importo-cvt" />
              </Field>
              <Field label="GGN *">
                <select className={inputCls} value={f.ggn} onChange={set("ggn")} data-testid="np-ggn">
                  <option value="">—</option>
                  {SI_NO.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label={`Durata GGN (mesi)${f.ggn === "SI" ? " *" : ""}`}>
                <select className={inputCls} value={f.durata_ggn} onChange={set("durata_ggn")} data-testid="np-durata-ggn">
                  <option value="">—</option>
                  {DURATE_GGN.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label={`Importo GGN${f.ggn === "SI" ? " *" : ""}`}>
                <input className={`${inputCls} no-spin`} type="text" inputMode="decimal" value={f.importo_ggn}
                  onChange={setImporto("importo_ggn")} onBlur={(e) => setF((x) => ({ ...x, importo_ggn: normalizzaImporto(e.target.value) }))} data-testid="np-importo-ggn" />
              </Field>
            </div>
          </section>

          <section>
            <div className="text-xs uppercase tracking-[0.16em] font-bold text-slate-400 mb-3">Date e orari</div>
            {incoerenza && (
              <div data-testid="np-errore-date"
                className="mb-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
                {incoerenza.messaggio}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Field label="Data ingresso *">
                <SelettoreData value={f.data_ingresso} onChange={(v) => setF((x) => ({ ...x, data_ingresso: v }))}
                  testId="np-data-ingresso" error={campo_errato === "data_ingresso"} />
              </Field>
              <Field label="Ora ingresso *">
                <input className={inputCls} type="time" value={f.ora_ingresso} onChange={set("ora_ingresso")}
                  data-testid="np-ora-ingresso" />
              </Field>
              <Field label="Data caricamento *">
                <SelettoreData value={f.data_caricamento} onChange={(v) => setF((x) => ({ ...x, data_caricamento: v }))}
                  testId="np-data-caricamento" error={campo_errato === "data_caricamento"} />
              </Field>
              <Field label="Ora esito">
                <input className={inputCls} type="time" value={f.ora_esito} onChange={set("ora_esito")}
                  data-testid="np-ora-esito" />
              </Field>
              <Field label="Data esito">
                <SelettoreData value={f.data_esito} onChange={(v) => setF((x) => ({ ...x, data_esito: v }))}
                  testId="np-data-esito" error={campo_errato === "data_esito"} />
              </Field>
              <Field label="Data integrazione">
                <SelettoreData value={f.data_integrazione} onChange={(v) => setF((x) => ({ ...x, data_integrazione: v }))}
                  testId="np-data-integrazione" error={campo_errato === "data_integrazione"} />
              </Field>
              <Field label="Ora integrazione">
                <input className={inputCls} type="time" value={f.ora_integrazione} onChange={set("ora_integrazione")}
                  data-testid="np-ora-integrazione" />
              </Field>
              <Field label="Data approvazione">
                <SelettoreData value={f.data_approvazione} onChange={(v) => setF((x) => ({ ...x, data_approvazione: v }))}
                  testId="np-data-approvazione" error={campo_errato === "data_approvazione"} />
              </Field>
              <Field label="Ora approvazione">
                <input className={inputCls} type="time" value={f.ora_approvazione} onChange={set("ora_approvazione")}
                  data-testid="np-ora-approvazione" />
              </Field>
            </div>
            <p className="mt-2 text-xs text-slate-400">
              Anno e mese di caricamento e approvazione vengono calcolati automaticamente dalle date.
            </p>
          </section>

          <section>
            <Field label="Note">
              <textarea className={`${inputCls} min-h-[70px]`} value={f.note} onChange={set("note")}
                data-testid="np-note" />
            </Field>
          </section>

          <section className={`rounded-lg border-2 p-4 transition-colors duration-200
            ${f.segnala_master ? "border-[#C8102E] bg-red-50/60" : "border-slate-200 bg-slate-50"}`}
            data-testid="np-segnalazione">
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input type="checkbox" checked={!!f.segnala_master} data-testid="np-segnala-master"
                onChange={(e) => setF((x) => ({ ...x, segnala_master: e.target.checked }))}
                className="h-4 w-4 accent-[#C8102E]" />
              <span className="text-sm font-semibold text-slate-800">
                {master ? <DestinatarioPratica numero={f.numero_originale || f.nr_pratica} /> : "Segnala a Gianluca"}
              </span>
            </label>
            {f.segnala_master && (
              <textarea className={`${inputCls} min-h-[60px] mt-3`} value={f.nota_segnalazione}
                onChange={set("nota_segnalazione")} data-testid="np-nota-segnalazione"
                placeholder="Perche' va vista: es. deroga da approvare, documenti mancanti..." />
            )}
          </section>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
          <div className="flex items-center gap-3 shrink-0">
            <button type="button" onClick={onClose} data-testid="np-annulla"
              className="text-sm px-4 py-2 rounded-md border border-slate-200 bg-white text-slate-600
                hover:text-slate-900 hover:border-slate-300 transition-colors duration-200">
              Annulla
            </button>
            <button type="submit" disabled={saving || mancanti.length > 0 || !!incoerenza} data-testid="np-salva"
              className="text-sm px-5 py-2 rounded-md bg-sky-600 text-white font-semibold
                hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200">
              {saving ? "Salvataggio…" : modo === "recupera" ? "Aggiorna pratica" : "Salva pratica"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

/** Il Master segnala a chi ha inserito la pratica nel gestionale. */
function DestinatarioPratica({ numero }) {
  const [utente, setUtente] = useState(null);
  useEffect(() => {
    if (!numero) { setUtente(null); return; }
    api.get(`/notifiche/destinatario-pratica/${encodeURIComponent(numero)}`)
      .then((r) => setUtente(r.data.utente)).catch(() => setUtente(null));
  }, [numero]);
  if (!utente) return <>Segnala <span className="text-amber-700 font-normal" data-testid="np-destinatario-mancante">· nessun utente ha inserito questa pratica: arriva ai Master</span></>;
  return <span data-testid="np-destinatario">Segnala a {utente.name}</span>;
}
